import { Injectable, InjectionToken, inject } from '@angular/core';

export interface ResolvedServiceLocation {
    path: string;
    baseUrl: string;
    versionHeader?: { name: string; value: string };
}

export type InfraPreset = 'b2e' | 'b2c' | 'b2b' | 'b2eadmin' | 'azureDefault';

/**
 * Should correspond to variables starting with 'RWC_CONFIG_' in app_settings
 * @see https://gitlab.com/diemobiliar/it/rwc/deliver/rwc-deliver-stack/-/blob/main/rwc-deliver-iaclib/src/module/app/main.tf
 * @see https://gitlab.com/diemobiliar/it/rwc/deliver/rwc-deliver-stack/-/blob/main/rwc-deliver-server-jslib/src/config/config.service.ts
 */
export interface SPAConfiguration {
    apiGatewayCloud?: string;
    apiGatewayOnPrem?: string;
    clientId?: string;
    infraPreset?: InfraPreset;
    isCloudApp?: 'true';
    stage?: Stage;
    tkNameId?: string;
    topologyEndpoint?: string;
    version?: string;
    [key: string]: string | number | undefined;
}

export type Stage = 'prod' | 'preprod' | 'sit' | 'local-dev';

type CloudConfig = keyof SPAConfiguration;

export const CUSTOM_CONFIG = new InjectionToken<SPAConfiguration>(
    'Custom configuration. If provided will not read config from backend.'
);
export const LOAD_CONFIG_MESSAGE =
    'Configuration should be loaded by including <script src="rwc/config.js"></script> as the first <script> tag in the SPAs index.html file.\n' +
    'Careful: _<script> should be the last element in <head> and must be after <href>.';

declare global {
    interface Window {
        MOBI_RWC_CONFIG: SPAConfiguration;
    }
}

/**
 * Will get the configuration from the backend or from the given custom configuration if provided.
 */
@Injectable({ providedIn: 'root' })
export class ConfigurationService {
    #customConfig = inject(CUSTOM_CONFIG, { optional: true });

    getSync<K extends CloudConfig>(property: K): SPAConfiguration[K] {
        return RwcConfig.get(property, this.#customConfig);
    }

    isStage(stage: Stage): boolean {
        return RwcConfig.isStage(stage, this.#customConfig);
    }

    isProduction(): boolean {
        return RwcConfig.isProduction(this.#customConfig);
    }

    getTkNameId(): string {
        return RwcConfig.getTkId(this.#customConfig);
    }
}

/**
 * Reads the RWC {@link ConfigurationService} from the global window variable.
 * Prefer using the `ConfigurationService` instead, this is only for cases where you cannot inject the service, like outside Angular context.
 * @param customConfig Optional custom configuration that will override the one from the window variable.
 */
function readRwcConfig(customConfig?: SPAConfiguration | null): SPAConfiguration {
    const configFromWindow = window.document.defaultView?.['MOBI_RWC_CONFIG'] as SPAConfiguration | undefined;
    if (!configFromWindow && !customConfig) {
        throw new Error('Configuration not available. ' + LOAD_CONFIG_MESSAGE);
    }
    return { ...configFromWindow, ...customConfig };
}

/**
 * Prefer using the {@link ConfigurationService} instead, this is only for cases where you cannot inject the service, like outside Angular context.
 *
 * Read configuration given by the backend.
 */
export class RwcConfig {
    static get<K extends CloudConfig>(property: K, customConfig?: SPAConfiguration | null): SPAConfiguration[K] {
        return readRwcConfig(customConfig)[property];
    }

    static getTkId(customConfig?: SPAConfiguration | null): string {
        // dash case is for on prem, camel case in azure
        return RwcConfig.get('tkNameId', customConfig) || (RwcConfig.get('tk-name-id', customConfig) as string);
    }

    static isStage(stage: Stage, customConfig?: SPAConfiguration | null): boolean {
        return RwcConfig.get('stage', customConfig) === stage;
    }

    static isProduction(customConfig?: SPAConfiguration | null): boolean {
        // 'ENV' is for on prem, 'stage' is for azure
        return (
            RwcConfig.isStage('prod', customConfig) ||
            RwcConfig.get('ENV', customConfig) === 'P' ||
            RwcConfig.get('ENV', customConfig) === 'p'
        );
    }
}
